<template>
  <div
    class="VisionRatingScale"
    :class="[
      `VisionRatingScale--height-${props.height}`,
      `VisionRatingScale--theme-${props.theme}`,
      `VisionRatingScale--colour-${props.colour}`,
    ]"
  >
    <div
      class="VisionRatingScale__rating-container"
      :style="{
        '--scale-left-width': `${computedWidth.left}%`,
        '--scale-right-width': `${computedWidth.right}%`,
      }"
    >
      <span class="VisionRatingScale__rating-line" />
    </div>
    <div class="VisionRatingScale__text">
      <span class="VisionRatingScale__title">{{ props.titleLeft }}</span>
      <span
        v-if="props.titleCenter"
        class="VisionRatingScale__title VisionRatingScale__title-center"
      >
        {{ props.titleCenter }}
      </span>
      <span class="VisionRatingScale__title">{{ props.titleRight }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

interface RatingProps {
  ratingLeft: number
  ratingRight: number
  height?: 'thin' | 'regular'
  theme?: 'gradient' | 'gradient-light' | 'solid'
  colour?: 'black' | 'white'
  titleLeft?: string
  titleCenter?: string
  titleRight?: string
}

const props = withDefaults(defineProps<RatingProps>(), {
  ratingLeft: 7,
  ratingRight: 7,
  height: 'thin',
  theme: 'gradient',
  colour: 'black',
  titleLeft: 'Left',
  titleCenter: '',
  titleRight: 'Right',
})

const computedWidth = computed(() => {
  return {
    left: Math.max(0, Math.min(10, props.ratingLeft)) * 10 / 2,
    right: Math.max(0, Math.min(10, props.ratingRight)) * 10 / 2,
  }
})
</script>

<style lang="postcss" scoped>
.VisionRatingScale {
  --vision-rating-scale-fill: var(
    --vision-rating-scale-fill-override,
    theme('colors.black')
  );
  --vision-rating-scale-bg-fill: var(
    --vision-rating-scale-bg-fill-override,
    theme('colors.grey.400')
  );
  --vision-rating-scale-height: var(
    --vision-rating-scale-height-override,
    theme('height.1')
  );

  @apply w-full max-w-full;
}

.VisionRatingScale__rating-container {
  @apply w-full items-center flex gap-1 h-[var(--vision-rating-scale-height)];

  &::before,
  &::after {
    @apply content-[''] h-1/4 min-h-[2px] rounded-full
    flex-grow-0 bg-[var(--vision-rating-scale-bg-fill)];
    min-width: 1%;
  }

  &::before {
    width: calc(50% - var(--scale-left-width))
  }

  &::after {
    width: calc(50% - var(--scale-right-width));
  }
}

.VisionRatingScale__rating-line {
    @apply h-full flex-grow
    transition-[width] duration-300 rounded-full;
    background: var(--vision-rating-scale-fill);
}

.VisionRatingScale__text {
  @apply flex justify-between mt-2;
}

.VisionRatingScale__title {
    @apply flex-grow
    text-grey-600 font-fragment-mono
    text-xxs last:text-right;
}

.VisionRatingScale__title-center {
    @apply flex-grow-0 text-center;
}

/**
  MODIFIERS
  --theme
  --colour
  --height
  */

/* Theme */
.VisionRatingScale--theme-gradient {
  .VisionRatingScale__rating-line {
    @apply bg-gradient;
  }
}

.VisionRatingScale--theme-gradient-light {
  .VisionRatingScale__rating-line{
    @apply bg-gradient-light;
  }
}

/* Colour */
.VisionRatingScale--colour-white {
  --vision-rating-scale-fill: var(
    --vision-rating-scale-fill-override,
    theme('colors.white')
  );
  --vision-rating-scale-bg-fill: var(
    --vision-rating-scale-bg-fill-override,
    theme('colors.grey.700')
  );

  .VisionRatingScale__text {
    @apply text-white;
  }

  .VisionRatingScale__title {
    @apply text-grey;
  }
}

.VisionRatingScale--height-regular {
  --vision-rating-scale-height: var(
    --vision-rating-scale-height-override,
    theme('height.2')
  );
}
</style>
